// Servicio para gestionar datos de demo en IndexedDB
export class DemoDataService {
  static DB_NAME = 'DarphaDemo';
  static DB_VERSION = 1;
  static STORES = {
    REPORTS: 'demoReports',
    STATISTICS: 'demoStatistics',
    PLANT_INFO: 'demoPlantInfo',
    CONFIG: 'demoConfig'
  };

  // Inicializar base de datos IndexedDB
  static async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Crear stores si no existen
        Object.values(this.STORES).forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
            
            // Crear índices específicos por store
            if (storeName === this.STORES.REPORTS) {
              store.createIndex('idreporte', 'idreporte', { unique: true });
              store.createIndex('fecha', 'fecha', { unique: false });
              store.createIndex('tipo', 'tipo', { unique: false });
            }
          }
        });
      };
    });
  }

  // Guardar datos en IndexedDB
  static async saveData(storeName, data) {
    const db = await this.initDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  // Obtener datos de IndexedDB
  static async getData(storeName, key = null) {
    const db = await this.initDB();
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      let request;
      
      if (key) {
        request = store.get(key);
      } else {
        request = store.getAll();
      }
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  // Limpiar todos los datos de demo
  static async clearDemoData() {
    const db = await this.initDB();
    
    const clearPromises = Object.values(this.STORES).map(storeName => {
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    });

    await Promise.all(clearPromises);
    console.log('🗑️ Datos de demo limpiados');
  }

  // Inicializar datos de demo con datos realistas
  static async initializeDemoData() {
    try {
      console.log('🔄 Inicializando datos de demo...');

      // Datos de la planta Darpha Fire Solutions
      const plantInfo = {
        id: 'plantInfo',
        idplanta: 48,
        nombreplanta: 'Darpha Fire Solutions',
        direccion: 'Av. Tecnológico #123, Guadalajara, Jalisco',
        telefono: '+52 33 1234-5678',
        email: 'contacto@darphafire.com',
        logo: '/images/darpha-logo.png',
        imagen: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=800&h=600&fit=crop'
      };

      // Estadísticas de ejemplo
      const statistics = {
        id: 'statistics',
        reportesCompletados: {
          completados: 2,
          requeridos: 6
        },
        semanaActual: this.getCurrentWeekNumber(),
        reportesPorSemana: this.generateWeeklyReports(),
        colaboradores: ['Juan Pérez', 'María González', 'Carlos Rodríguez'],
        reportesPendientes: [
          { reporte_nombre: 'Semanal Bomba Jockey', completados: 0, requeridos: 1 },
          { reporte_nombre: 'Semanal Bomba Diesel', completados: 0, requeridos: 1 },
          { reporte_nombre: 'Semanal Sistemas Húmedos', completados: 0, requeridos: 1 },
          { reporte_nombre: 'Semanal Sistemas CO2', completados: 0, requeridos: 1 },
          { reporte_nombre: 'Semanal Tableros', completados: 0, requeridos: 1 },
          { reporte_nombre: 'Semanal Válvulas', completados: 0, requeridos: 1 }
        ]
      };

      // Generar reportes de ejemplo
      const reports = this.generateDemoReports();

      // Configuración de demo
      const config = {
        id: 'config',
        initialized: true,
        version: '1.0.0',
        lastUpdated: new Date().toISOString()
      };

      // Guardar todos los datos
      await this.saveData(this.STORES.PLANT_INFO, plantInfo);
      await this.saveData(this.STORES.STATISTICS, statistics);
      await this.saveData(this.STORES.CONFIG, config);

      // Guardar reportes individuales
      for (const report of reports) {
        await this.saveData(this.STORES.REPORTS, report);
      }

      console.log('✅ Datos de demo inicializados correctamente');
      return true;

    } catch (error) {
      console.error('❌ Error al inicializar datos de demo:', error);
      throw error;
    }
  }

  // Obtener todos los datos de demo organizados
  static async getDemoData() {
    try {
      const [plantInfo, statistics, reports, config] = await Promise.all([
        this.getData(this.STORES.PLANT_INFO, 'plantInfo'),
        this.getData(this.STORES.STATISTICS, 'statistics'),
        this.getData(this.STORES.REPORTS),
        this.getData(this.STORES.CONFIG, 'config')
      ]);

      if (!config?.initialized) {
        return null;
      }

      return {
        plantInfo,
        statistics,
        reports: reports || [],
        config,
        isDemo: true
      };

    } catch (error) {
      console.error('Error al obtener datos de demo:', error);
      return null;
    }
  }

  // Generar reportes de ejemplo realistas
  static generateDemoReports() {
    const reports = [];
    const currentDate = new Date();
    
    // Tipos de mantenimiento
    const tiposMantenimiento = [
      { id: 1, nombre: 'Mantenimiento Preventivo', clasificacion: 1 },
      { id: 2, nombre: 'Inspección de Seguridad', clasificacion: 1 },
      { id: 3, nombre: 'Mantenimiento Correctivo', clasificacion: 2 },
      { id: 4, nombre: 'Reparación de Emergencia', clasificacion: 2 }
    ];

    const inspectores = [
      'Juan Pérez Martínez',
      'María González López',
      'Carlos Rodríguez Silva',
      'Ana Torres Jiménez',
      'Luis Mendoza Cruz'
    ];

    const equipos = [
      'Extintores CO2 - Zona A',
      'Sistema Hidráulico Principal',
      'Válvulas de Seguridad - Sector 1',
      'Bombas de Agua - Edificio Central',
      'Detectores de Humo - Oficinas',
      'Sprinklers - Almacén General',
      'Mangueras contra incendios',
      'Panel de Control Central'
    ];

    // Generar reportes de los últimos 30 días
    for (let i = 0; i < 25; i++) {
      const fecha = new Date(currentDate);
      fecha.setDate(fecha.getDate() - Math.floor(Math.random() * 30));
      
      const tipoMantenimiento = tiposMantenimiento[Math.floor(Math.random() * tiposMantenimiento.length)];
      const inspector = inspectores[Math.floor(Math.random() * inspectores.length)];
      const equipo = equipos[Math.floor(Math.random() * equipos.length)];
      
      const report = {
        id: `demo-report-${i + 1}`,
        idreporte: 1000 + i,
        idempresacliente: 48,
        idplanta: 48,
        idinspector: Math.floor(Math.random() * 5) + 1,
        idsupervisor: 1,
        observaciones: `Reporte de ${tipoMantenimiento.nombre.toLowerCase()} para ${equipo}`,
        estadofirmainspector: true,
        estadofirmasupervisor: Math.random() > 0.3, // 70% firmados, 30% sin firmar
        fechacreacion: fecha.toISOString(),
        fechaactualizacion: fecha.toISOString(),
        respuesta: {
          idreporte: 1000 + i,
          idtipomantenimiento: tipoMantenimiento.id,
          tipoMantenimiento: tipoMantenimiento.nombre,
          idclasificacionmantenimiento: tipoMantenimiento.clasificacion,
          fecha: fecha.toISOString().split('T')[0],
          estadofirmainspector: "true",
          estadofirmasupervisor: Math.random() > 0.3 ? "true" : "false",
          inspector: inspector,
          supervisor: 'Roberto Supervisor Demo',
          empresa: 'Darpha Fire Solutions',
          planta: 'Planta Principal',
          equipoInspeccionado: equipo,
          secciones: this.generateReportSections(equipo, tipoMantenimiento.nombre)
        }
      };

      reports.push(report);
    }

    return reports;
  }

  // Generar secciones de reporte
  static generateReportSections(equipo, tipoMantenimiento) {
    const secciones = [
      {
        idseccionreporte: 1,
        nombreseccion: 'Información General',
        categoriaId: 1,
        categoriaNombre: 'General',
        items: [
          {
            iditem: 1,
            nombreitem: 'Equipo inspeccionado',
            valor: equipo,
            observaciones: `Inspección realizada en ${equipo}`
          },
          {
            iditem: 2,
            nombreitem: 'Tipo de mantenimiento',
            valor: tipoMantenimiento,
            observaciones: 'Mantenimiento realizado según programa'
          },
          {
            iditem: 3,
            nombreitem: 'Estado general',
            valor: Math.random() > 0.2 ? 'Bueno' : 'Requiere atención',
            observaciones: 'Equipo en condiciones operativas'
          }
        ]
      },
      {
        idseccionreporte: 2,
        nombreseccion: 'Checklist',
        categoriaId: 2,
        categoriaNombre: 'Verificación',
        items: this.generateChecklistItems()
      }
    ];

    return secciones;
  }

  // Generar items de checklist
  static generateChecklistItems() {
    const checklistItems = [
      'Funcionamiento correcto',
      'Sin daños visibles',
      'Presión adecuada',
      'Conexiones seguras',
      'Limpieza general',
      'Documentación actualizada'
    ];

    return checklistItems.map((item, index) => ({
      iditem: index + 10,
      nombreitem: item,
      valor: Math.random() > 0.1 ? 'Sí' : 'No',
      observaciones: Math.random() > 0.7 ? 'Revisar en próxima inspección' : 'Conforme'
    }));
  }

  // Generar datos de reportes por semana
  static generateWeeklyReports() {
    const weeklyData = [];
    const currentWeek = this.getCurrentWeekNumber();
    
    for (let i = 4; i >= 0; i--) {
      const week = currentWeek - i;
      const reportCount = Math.floor(Math.random() * 8) + 3; // Entre 3 y 10 reportes
      
      weeklyData.push({
        semana: week,
        reportes: reportCount,
        fecha: `Semana ${week}`
      });
    }
    
    return weeklyData;
  }

  // Obtener número de semana actual
  static getCurrentWeekNumber(date = new Date()) {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - startOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
  }

  // Función para simular delay de API (opcional)
  static async simulateApiDelay(min = 500, max = 1500) {
    const delay = Math.random() * (max - min) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  // Obtener reportes filtrados (simula endpoint de API)
  static async getDemoReportsFiltered(filters = {}) {
    await this.simulateApiDelay(300, 800);
    
    const allReports = await this.getData(this.STORES.REPORTS);
    let filteredReports = allReports || [];

    // Aplicar filtros si se proporcionan
    if (filters.tipo_mantenimiento) {
      filteredReports = filteredReports.filter(report => 
        report.respuesta?.idtipomantenimiento === filters.tipo_mantenimiento
      );
    }

    if (filters.fecha_inicio && filters.fecha_fin) {
      filteredReports = filteredReports.filter(report => {
        const reportDate = new Date(report.respuesta?.fecha);
        const startDate = new Date(filters.fecha_inicio);
        const endDate = new Date(filters.fecha_fin);
        return reportDate >= startDate && reportDate <= endDate;
      });
    }

    // Simular estructura de respuesta de API
    return filteredReports.map(report => ({
      idreporte: report.idreporte,
      fecha: report.respuesta?.fecha,
      inspector: report.respuesta?.inspector,
      tipoMantenimiento: report.respuesta?.tipoMantenimiento,
      empresa: report.respuesta?.empresa
    }));
  }

  // Obtener estadísticas de reportes completados (simula endpoint de API)
  static async getDemoReportsCompleted(idPlanta, date) {
    await this.simulateApiDelay(200, 600);
    
    const statistics = await this.getData(this.STORES.STATISTICS, 'statistics');
    
    if (!statistics) {
      return [];
    }

    // Simular estructura de respuesta de API que coincida con lo que espera el componente
    return statistics.reportesPendientes?.map(reporte => ({
      reporte_nombre: reporte.reporte_nombre,
      completados: reporte.completados,
      requeridos: reporte.requeridos
    })) || [];
  }
}
